import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldIcon, FileTextIcon } from 'lucide-react';
const privacyContent = {
  title: 'Privacy Policy',
  lastUpdated: 'January 2025',
  sections: [{
    heading: 'Information We Collect',
    body: 'We collect your name, email, and payment details during purchase. Secure third-party processors handle all payments.'
  }, {
    heading: 'How We Use Your Information',
    body: 'Your data is used solely to deliver your product and provide support. We never sell your personal data.'
  }, {
    heading: 'Cookies & Tracking',
    body: 'We use essential cookies for site functionality and analytics. You can disable these in your browser settings.'
  }, {
    heading: 'Data Security',
    body: 'We use industry-standard 256-bit SSL encryption to protect your information.'
  }, {
    heading: 'Your Rights',
    body: 'You can access, correct, or delete your data at any time by contacting our support team.'
  }, {
    heading: 'Changes to This Policy',
    body: 'We may update this policy periodically. Changes will be posted here with a new revision date.'
  }]
};
const termsContent = {
  title: 'Terms of Service',
  lastUpdated: 'January 2025',
  sections: [{
    heading: 'Acceptance of Terms',
    body: 'By purchasing, you agree to these Terms of Service. Do not purchase if you disagree.'
  }, {
    heading: 'Digital Product Delivery',
    body: 'You receive instant access upon payment. All digital sales are final.'
  }, {
    heading: 'Intellectual Property',
    body: 'All content is protected by copyright. Redistribution or resale is strictly prohibited.'
  }, {
    heading: 'Medical Disclaimer',
    body: 'This is an educational resource, not a substitute for professional medical advice or treatment.'
  }, {
    heading: 'Limitation of Liability',
    body: 'We provide this product "as is" and are not liable for any damages arising from its use.'
  }, {
    heading: 'Governing Law',
    body: 'These terms are governed by applicable laws in the relevant jurisdiction.'
  }]
};
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="w-full bg-dark-navy border-t border-teal-accent/10 py-8 px-6 md:px-12" data-id="element-30">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-31">
          <p className="text-stone-500 text-sm" data-id="element-32">
            © {new Date().getFullYear()} The Biological Auto-Scan. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-33">
            <button onClick={() => setActiveModal('privacy')} className="text-stone-400 hover:text-teal-accent text-sm transition-colors flex items-center gap-1.5" data-id="element-34">
              <ShieldIcon className="w-3.5 h-3.5" data-id="element-35" />
              Privacy Policy
            </button>
            <span className="text-stone-600" data-id="element-36">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-stone-400 hover:text-teal-accent text-sm transition-colors flex items-center gap-1.5" data-id="element-37">
              <FileTextIcon className="w-3.5 h-3.5" data-id="element-38" />
              Terms of Service
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-4 pt-4 border-t border-white/5" data-id="element-39">
          <p className="text-stone-500 text-xs text-center leading-relaxed" data-id="element-40">
            Disclaimer: This product is for educational purposes only and does
            not substitute professional medical advice. Always consult a
            qualified healthcare provider.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-41">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-42">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3
        }} className="bg-dark-navy-light border border-teal-accent/20 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden cyan-glow" onClick={e => e.stopPropagation()} data-id="element-43">
              <div className="flex items-center justify-between p-6 border-b border-teal-accent/10" data-id="element-44">
                <h2 className="text-xl font-bold text-white" data-id="element-45">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-stone-400 hover:text-white transition-colors p-1" data-id="element-46">
                  <XIcon className="w-5 h-5" data-id="element-47" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] space-y-6" data-id="element-48">
                <p className="text-stone-500 text-sm" data-id="element-49">
                  Last updated: {content.lastUpdated}
                </p>
                {content.sections.map((section, index) => <div key={index} className="space-y-2" data-id="element-50">
                    <h3 className="text-teal-accent font-semibold text-base" data-id="element-51">
                      {section.heading}
                    </h3>
                    <p className="text-stone-300 text-sm leading-relaxed" data-id="element-52">
                      {section.body}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}